import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AccountBalanceWalletOutlined, Close, LockOutlined } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { api } from "../../config/api";
import { WALLET_BALANCE_KEY, useWalletBalance } from "../../hooks/useWalletBalance";
import { formatCurrency } from "../features/dashboard/DashboardUI";
import { getErrorMessage } from "../features/orders/orderUtils";

const MIN = 100;
const MAX = 5000000;
const STEP = 100;
const PRESETS = [500, 1000, 2000, 5000, 10000];

const validate = (amount: number) => {
  if (!amount) return "Enter an amount";
  if (amount < MIN) return `Minimum recharge is ₹${MIN}`;
  if (amount > MAX) return "Maximum recharge is ₹50,00,000";
  if (amount % STEP !== 0) return `Enter a multiple of ₹${STEP}`;
  return "";
};

const AddWalletModal = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
  const queryClient = useQueryClient();
  const balance = useWalletBalance(open);
  const [value, setValue] = useState("1000");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const amount = Number(value) || 0;
  const amountError = validate(amount);

  const close = () => {
    if (busy) return;
    setError("");
    handleClose();
  };

  // Same flow as before: create a Razorpay order, open checkout, then confirm the payment with our API.
  const pay = async () => {
    if (amountError) return;
    if (!window.Razorpay) {
      setError("The payment window couldn't load. Check your connection or disable ad blockers, then try again.");
      return;
    }
    setError("");
    setBusy(true);
    const currency = "INR";
    try {
      const res = await api.post("/payment/razorpayOrderAPIView/", { amount: String(amount), currency });
      const order = res.data.data;
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency,
        name: "Hipship",
        description: "Wallet recharge",
        order_id: order.id,
        handler: async (response: any) => {
          try {
            await api.post("/payment/razorpayPaymentStatus/", {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
              amount: order.amount / 100,
            });
            toast.success(`₹${(order.amount / 100).toLocaleString("en-IN")} added to your wallet`);
            queryClient.invalidateQueries({ queryKey: WALLET_BALANCE_KEY });
            queryClient.invalidateQueries({ queryKey: ["billing"] });
            setBusy(false);
            handleClose();
          } catch (err) {
            setBusy(false);
            setError(
              getErrorMessage(
                err,
                "Payment received, but we couldn't confirm it yet. Don't pay again. Your balance will update shortly; contact support if it doesn't."
              )
            );
          }
        },
        modal: { ondismiss: () => setBusy(false) },
        theme: { color: "#1f7aec" },
      });
      rzp.on("payment.failed", (response: any) => {
        setBusy(false);
        setError(response?.error?.description || "The payment failed. No money was taken. Please try again.");
      });
      rzp.open();
    } catch (err) {
      setBusy(false);
      setError(getErrorMessage(err, "Couldn't start the payment. Please try again."));
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 3, pb: 2, background: "linear-gradient(135deg, #1f7aec 0%, #1554b3 100%)", color: "#fff", position: "relative" }}>
        <IconButton onClick={close} disabled={busy} sx={{ position: "absolute", top: 8, right: 8, color: "#fff" }} aria-label="Close">
          <Close />
        </IconButton>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <AccountBalanceWalletOutlined />
          <Typography fontWeight={700} fontSize={18}>
            Recharge wallet
          </Typography>
        </Stack>
        <Typography fontSize={13} sx={{ opacity: 0.85 }}>
          Current balance
        </Typography>
        <Typography fontSize={24} fontWeight={700}>
          {balance.isLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : formatCurrency(balance.data ?? 0)}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <TextField
          fullWidth
          autoFocus
          label="Amount to add"
          value={value}
          onChange={(e) => /^\d{0,7}$/.test(e.target.value) && setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && pay()}
          error={!!value && !!amountError}
          helperText={(value && amountError) || `Multiples of ₹${STEP}, from ₹${MIN} to ₹50,00,000`}
          inputProps={{ inputMode: "numeric" }}
          InputProps={{
            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            sx: { fontSize: 20, fontWeight: 600 },
          }}
        />
        <Stack direction="row" gap={1} mt={1.5} flexWrap="wrap">
          {PRESETS.map((p) => (
            <Chip
              key={p}
              label={`₹${p.toLocaleString("en-IN")}`}
              onClick={() => setValue(String(p))}
              color={amount === p ? "primary" : "default"}
              variant={amount === p ? "filled" : "outlined"}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Stack>
        {!amountError && balance.data !== undefined && (
          <Typography fontSize={13} color="text.secondary" mt={2}>
            New balance after recharge: <b>{formatCurrency((balance.data ?? 0) + amount)}</b>
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", alignItems: "stretch", gap: 1 }}>
        <Button
          variant="contained"
          size="large"
          disableElevation
          onClick={pay}
          disabled={!!amountError || busy}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
        >
          {busy ? "Waiting for payment…" : amountError ? "Pay" : `Pay ₹${amount.toLocaleString("en-IN")}`}
        </Button>
        <Typography fontSize={12} color="text.secondary" display="flex" alignItems="center" justifyContent="center" gap={0.5} m={0}>
          <LockOutlined sx={{ fontSize: 14 }} /> Secure payment by Razorpay
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default AddWalletModal;
